import React, {useState, useEffect} from 'react';
import {useLocation, useNavigate, Navigate} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import {sendGAPageview, fetchPostAuthSafe} from '../app/utils';

import {CoverPageCard} from '../components/CoverPageCard';
import {SearchableSelectTable} from '../components/SearchableSelectTable';
import {DownStatusIcon} from '../components/DownStatusIcon';
import {checkAuthentication} from '../appSlice';
import {clearCabviewUiCache} from './Cabview/cabviewUiLocalCache';
import {PASSKEY_UI_ENABLED, isWebAuthnAvailable, loginWithPasskey, setPasskeyNudgeLoginFlag} from '../app/webauthn';
import {normalizePhoneNumber, validatePhoneNumber} from '../components/PhoneVerificationSection';

// Remembered on this device, so the sign-in page opens in the language last chosen
const LOGIN_LANGUAGE_KEY = 'loginLanguage';

/**
 * The sign-in page's language before anyone is signed in: the one chosen on this device, else the
 * browser's (Spanish browsers get Spanish), else English.
 *
 * @return {string} en or es
 */
function initialLoginLanguage() {
  try {
    const saved = window.localStorage.getItem(LOGIN_LANGUAGE_KEY);
    if (saved === 'en' || saved === 'es') {
      return saved;
    }
  } catch (error) {
    // Storage blocked; fall back to the browser language
  }
  return String(navigator.language || '')
    .toLowerCase()
    .startsWith('es')
    ? 'es'
    : 'en';
}

function Login() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const authenticationStatus = useSelector((state) => {
    return state.app.authenticationStatus;
  });

  const [inputs, setInputs] = useState({
    username: '',
    password: '',
  });
  const [status, setStatus] = useState('');
  const [errorType, setErrorType] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [customerChoice, setCustomerChoice] = useState({options: [], activeCustomer: '', redirectTo: ''});
  const [customerSelecting, setCustomerSelecting] = useState('');
  const [customerSelectError, setCustomerSelectError] = useState('');
  const [passkeySupported, setPasskeySupported] = useState(false);
  const [retryAfterSec, setRetryAfterSec] = useState(0);
  // Names of the Organization Admins a locked out user can contact
  const [lockoutContacts, setLockoutContacts] = useState([]);
  // Whether the user's organization requires multi-factor authentication, for the lockout message
  const [lockoutMfaRequired, setLockoutMfaRequired] = useState(false);
  // Sign-in code step, shown after a correct password when the account has codes on
  // phoneNumber: the number typed in when setting codes up by text at this sign-in, for resends
  const [codeStep, setCodeStep] = useState({
    factors: [],
    maskedDestination: '',
    sending: false,
    enrollment: false,
    phoneNumber: '',
  });
  const [setupPhone, setSetupPhone] = useState('');
  const [setupPhoneError, setSetupPhoneError] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [codeMessage, setCodeMessage] = useState({text: '', isError: false});
  const [resendWaitSec, setResendWaitSec] = useState(0);
  const [language, setLanguage] = useState(initialLoginLanguage);

  /**
   * The text in the page's language.
   *
   * @param {string} english
   * @param {string} spanish
   * @return {string}
   */
  function tr(english, spanish) {
    return language === 'es' ? spanish : english;
  }

  function switchLanguage() {
    const next = language === 'es' ? 'en' : 'es';
    setLanguage(next);
    try {
      window.localStorage.setItem(LOGIN_LANGUAGE_KEY, next);
    } catch (error) {
      // Not remembered; the switch still applies to this visit
    }
  }

  const languageSwitch = (
    <div className='text-center mt-2'>
      <button type='button' className='btn btn-link btn-sm' onClick={switchLanguage}>
        {language === 'es' ? 'English' : 'Español'}
      </button>
    </div>
  );

  useEffect(async () => {
    document.title = 'IntelliCulture | Login';
    sendGAPageview(document.title, true);

    await dispatch(checkAuthentication());
    setPasskeySupported(PASSKEY_UI_ENABLED && isWebAuthnAvailable());
  }, []);

  // Count down the wait before another code can be sent
  useEffect(() => {
    if (resendWaitSec <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setResendWaitSec((seconds) => {
        return seconds - 1;
      });
    }, 1000);
    return () => {
      return clearTimeout(timer);
    };
  }, [resendWaitSec]);

  useEffect(async () => {
    if (!authenticationStatus.loggedIn) {
      dispatch({type: 'RESET'});
      clearCabviewUiCache(); // Cabview's localStorage cache isn't per-account; wipe it so a new login can't inherit it
      return null;
    } else {
      return <Navigate replace to='/' />;
    }
  }, [authenticationStatus]);

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    setInputs((values) => {
      return {...values, [name]: value};
    });
  };

  function handleAuthSuccess(result) {
    if (result.redirectTo) {
      window.location.href = result.redirectTo;
    } else if (location.state) {
      navigate(location.state.redirectTo);
    } else {
      navigate('/');
    }
  }

  function handleLoginSuccess(result) {
    // Let the user pick which customer to load before anything is rendered for one of them.
    if (Array.isArray(result.customerOptions) && result.customerOptions.length > 1) {
      setCustomerChoice({
        options: result.customerOptions,
        activeCustomer: result.activeCustomer || '',
        redirectTo: result.redirectTo || '',
      });
      if (result.userName) {
        setInputs((values) => {
          return {...values, username: result.userName};
        });
      }
      setStatus('selectCustomer');
      return;
    }

    handleAuthSuccess(result);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setStatus('submitting');
    setErrorType('');
    const postData = {
      username: inputs.username,
      password: inputs.password,
    };
    const response = await fetch('/auth', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(postData),
      cache: 'no-store',
    });
    const result = await response.json();
    if (result.codeRequired && result.enrollment === true && result.textSetupAvailable === true) {
      // Setting codes up at this sign-in, with a phone or the account email: ask which first
      setCodeStep({
        factors: result.factors || [],
        maskedDestination: '',
        sending: false,
        enrollment: true,
        phoneNumber: '',
      });
      setSetupPhone('');
      setSetupPhoneError('');
      setStatus('chooseSetup');
    } else if (result.codeRequired) {
      startCodeStep(result.factors || [], result.enrollment === true);
    } else if (typeof result.success != 'undefined' && result.success === true) {
      setPasskeyNudgeLoginFlag('pending');
      handleLoginSuccess(result);
    } else {
      setStatus('failure');
      setErrorType(result.error || '');
      setRetryAfterSec(result.retryAfterSec || 0);
      setLockoutContacts(result.contacts || []);
      setLockoutMfaRequired(result.mfaRequired === true);
    }
  }

  /**
   * Text for a sign-in code error from the server, in the page's language.
   * @param {Object} result - response from /auth/sendCode or /auth/verifyCode
   * @return {string}
   */
  function codeErrorText(result) {
    switch (result.error) {
      case 'wrongCode':
        return tr(
          `Wrong code, ${result.guessesLeft} tries left`,
          `Código incorrecto, quedan ${result.guessesLeft} intentos`
        );
      case 'lockedOut':
        return tr(
          'Too many wrong codes. Contact support@intelliculture.com or your administrator.',
          'Demasiados códigos incorrectos. Contacte a support@intelliculture.com o a su administrador.'
        );
      case 'codeExpired':
        return tr('That code has expired. Send a new code.', 'El código venció. Envíe un código nuevo.');
      case 'resendTooSoon':
        return tr(
          `Please wait ${result.retryAfterSec} seconds before sending another code.`,
          `Espere ${result.retryAfterSec} segundos para enviar otro código.`
        );
      case 'tooManySends': {
        const minutes = Math.ceil((result.retryAfterSec || 60) / 60);
        return tr(
          `Too many codes sent. Try again in ${minutes} minutes.`,
          `Demasiados códigos enviados. Intente de nuevo en ${minutes} minutos.`
        );
      }
      case 'dailyLimit':
        return tr(
          'Too many codes sent today. Contact your administrator.',
          'Demasiados códigos enviados hoy. Contacte a su administrador.'
        );
      case 'signInExpired':
        return tr(
          'Your sign-in expired. Please enter your password again.',
          'Su acceso venció. Ingrese su contraseña de nuevo.'
        );
      case 'account':
        return tr(
          'Account unavailable. Contact your administrator.',
          'Cuenta no disponible. Contacte a su administrador.'
        );
      case 'phoneOptedOut':
        return tr(
          'Your phone replied STOP to IntelliCulture sign-in texts. Reply START to the number your sign-in codes ' +
            'come from, then send a new code, or use "Email me instead".',
          'Su teléfono respondió STOP a los textos de acceso de IntelliCulture. Responda START al número del que ' +
            'le llegan los códigos de acceso y pida un código nuevo, o use "Enviar por correo".'
        );
      case 'phoneNotAllowed':
      case 'invalidPhone':
      case 'textNotConfigured':
        return tr(
          'We could not text your phone. Use "Email me instead".',
          'No pudimos enviar el texto. Use "Enviar por correo".'
        );
      default:
        return tr(
          'Could not send or check the code. Please try again.',
          'No se pudo enviar o verificar el código. Intente de nuevo.'
        );
    }
  }

  /**
   * Why a number typed in to set codes up by text could not be texted, in the page's language.
   * @param {string} error - from /auth/sendCode
   * @return {string}
   */
  function setupPhoneErrorText(error) {
    switch (error) {
      case 'phoneNotAllowed':
        return tr('Texts to this number are not allowed.', 'No se permiten textos a este número.');
      case 'phoneOptedOut':
        return tr(
          'This number replied STOP to IntelliCulture sign-in texts. Reply START to the number your sign-in codes ' +
            'come from, then try again, or use email.',
          'Este número respondió STOP a los textos de acceso de IntelliCulture. Responda START al número del que ' +
            'le llegan los códigos de acceso y vuelva a intentarlo, o use el correo.'
        );
      case 'textNotConfigured':
        return tr(
          'Text codes are not available right now. Use email.',
          'Los códigos por texto no están disponibles. Use el correo.'
        );
      default:
        return tr(
          'That number cannot receive texts. Enter a mobile number, e.g. +15551234567.',
          'Ese número no puede recibir textos. Ingrese un celular, p. ej. +15551234567.'
        );
    }
  }

  /**
   * Ask the server to send a sign-in code for the waiting sign-in.
   * @param {string} [factorType] - email or text; the server picks the first one when omitted
   * @param {string} [phoneNumber] - E.164, only when setting codes up by text at this sign-in
   */
  async function sendSignInCode(factorType, phoneNumber) {
    setCodeStep((current) => {
      return {...current, sending: true};
    });
    setCodeMessage({text: '', isError: false});

    let result;
    try {
      const response = await fetch('/auth/sendCode', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(phoneNumber ? {factorType, phoneNumber} : {factorType}),
        cache: 'no-store',
      });
      result = await response.json();
    } catch (error) {
      result = {success: false};
    }

    setCodeStep((current) => {
      return {...current, sending: false, maskedDestination: result.maskedDestination || current.maskedDestination};
    });
    if (result.success) {
      setResendWaitSec(result.resendAfterSec || 30);
      setCodeMessage({
        text: tr(`Code sent to ${result.maskedDestination}`, `Código enviado a ${result.maskedDestination}`),
        isError: false,
      });
    } else {
      if (result.retryAfterSec) {
        setResendWaitSec(result.retryAfterSec);
      }
      const textSetupRefused =
        phoneNumber && ['invalidPhone', 'phoneNotAllowed', 'phoneOptedOut', 'textNotConfigured'].includes(result.error);
      if (textSetupRefused) {
        // Back to the number, to fix it or pick email
        setSetupPhoneError(setupPhoneErrorText(result.error));
        setStatus('enterSetupPhone');
        return;
      }
      setCodeMessage({text: codeErrorText(result), isError: true});
      if (result.error === 'signInExpired' || result.error === 'lockedOut') {
        leaveCodeStep();
        if (result.error === 'lockedOut') {
          setStatus('failure');
          setErrorType('lockedOut');
          setLockoutContacts(result.contacts || []);
          setLockoutMfaRequired(result.mfaRequired === true);
        }
      }
    }
  }

  // Setting codes up at sign-in by text: send the first code to the number typed in
  function sendSetupPhoneCode() {
    const phoneNumber = normalizePhoneNumber(setupPhone);
    if (!validatePhoneNumber(phoneNumber)) {
      setSetupPhoneError(setupPhoneErrorText('invalidPhone'));
      return;
    }
    setSetupPhoneError('');
    setCodeStep((current) => {
      return {...current, maskedDestination: '', phoneNumber};
    });
    setCodeInput('');
    setStatus('enterCode');
    sendSignInCode('text', phoneNumber);
  }

  /**
   * Show the code step and send the first code.
   * @param {Object[]} factors - [{factorType, maskedDestination}]
   * @param {boolean} enrollment - the user's organization requires codes and this sign-in sets them up
   */
  function startCodeStep(factors, enrollment) {
    setCodeStep({
      factors,
      maskedDestination: factors[0]?.maskedDestination || '',
      sending: false,
      enrollment,
      phoneNumber: '',
    });
    setCodeInput('');
    setStatus('enterCode');
    sendSignInCode(factors[0]?.factorType);
  }

  // Back to the password form
  function leaveCodeStep() {
    setCodeInput('');
    setResendWaitSec(0);
    setInputs((values) => {
      return {...values, password: ''};
    });
    setStatus('');
  }

  async function handleCodeSubmit(event) {
    event.preventDefault();
    setStatus('verifyingCode');
    setCodeMessage({text: '', isError: false});

    let result;
    try {
      // fetch with an options object, so inside the CabView app the platform header is sent and the app
      // saves the session ID and token from this /auth response
      const response = await fetch('/auth/verifyCode', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({code: codeInput}),
        cache: 'no-store',
      });
      result = await response.json();
    } catch (error) {
      result = {success: false};
    }

    if (result.success === true) {
      setPasskeyNudgeLoginFlag('pending');
      handleLoginSuccess(result);
      return;
    }

    setStatus('enterCode');
    setCodeInput('');
    setCodeMessage({text: codeErrorText(result), isError: true});
    if (result.error === 'signInExpired' || result.error === 'account' || result.error === 'lockedOut') {
      leaveCodeStep();
      setStatus('failure');
      setErrorType(result.error === 'signInExpired' ? 'signInExpired' : result.error);
      setLockoutContacts(result.contacts || []);
      setLockoutMfaRequired(result.mfaRequired === true);
    }
  }

  // Continue to the app once a customer is settled on
  function continueAfterLogin(redirectTo) {
    handleAuthSuccess({redirectTo});
  }

  // Handle a customer pick on the post-login selection screen
  async function customerSelectClick(customerName) {
    if (customerSelecting) {
      return;
    }

    // The session already sits on the active customer, so choosing it needs no round trip
    if (customerName === customerChoice.activeCustomer) {
      continueAfterLogin(customerChoice.redirectTo);
      return;
    }

    setCustomerSelecting(customerName);
    setCustomerSelectError('');

    const options = {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({database: customerName}),
    };

    let result;
    try {
      const response = await fetchPostAuthSafe(
        '/settings/changeDatabase',
        options,
        inputs.username.toLowerCase().trim(),
        customerChoice.activeCustomer
      );
      result = await response.json();
    } catch (error) {
      console.error('[Login] customer selection failed:', error);
      result = null;
    }

    if (!result || !result.success) {
      setCustomerSelecting('');
      setCustomerSelectError((result && result.errorMsg) || 'Could not load that customer');
      return;
    }

    continueAfterLogin(customerChoice.redirectTo);
  }

  /**
   * Map WebAuthn login error codes to a Login UI errorType.
   * @param {string} [error]
   * @return {string}
   */
  function mapPasskeyErrorType(error) {
    if (error === 'username' || error === 'account') {
      return error;
    }
    if (
      error === 'passkey_not_found' ||
      error === 'missing_challenge' ||
      error === 'missing_credential' ||
      error === 'options_failed' ||
      error === 'verification_failed'
    ) {
      return error;
    }
    // Unknown / raw authenticator errors still show the generic passkey message
    return 'passkey';
  }

  async function handlePasskeyLogin() {
    setStatus('submitting');
    setErrorType('');
    try {
      const result = await loginWithPasskey();
      if (typeof result.success != 'undefined' && result.success === true) {
        setPasskeyNudgeLoginFlag('skip');
        handleLoginSuccess(result);
      } else {
        setStatus('failure');
        setErrorType(mapPasskeyErrorType(result.error));
      }
    } catch (error) {
      console.error('Passkey login failed:', error);
      setStatus('failure');
      setErrorType('passkey');
    }
  }

  if (status == 'chooseSetup' || status == 'enterSetupPhone') {
    const emailFactor = codeStep.factors.find((factor) => {
      return factor.factorType === 'email';
    });
    return (
      <CoverPageCard>
        <div className='text-center'>
          <h1 className='h4 text-gray-900 mb-2'>
            {tr('Set up multi-factor authentication', 'Configure la autenticación multifactor')}
          </h1>
          <p className='text-gray-700' style={{fontSize: '14px'}}>
            {tr(
              'Your organization requires a code after your password. Choose where we send it.',
              'Su organización exige un código después de su contraseña. Elija dónde se lo enviamos.'
            )}
          </p>
        </div>
        <hr />
        {status == 'chooseSetup' ? (
          <>
            <button
              type='button'
              className='btn btn-primary btn-user btn-block'
              onClick={() => {
                setSetupPhoneError('');
                setStatus('enterSetupPhone');
              }}
            >
              {tr('Text message', 'Mensaje de texto')}
            </button>
            {emailFactor && (
              <button
                type='button'
                className='btn btn-outline-primary btn-user btn-block'
                onClick={() => {
                  startCodeStep(codeStep.factors, true);
                }}
              >
                {tr(`Email to ${emailFactor.maskedDestination}`, `Correo a ${emailFactor.maskedDestination}`)}
              </button>
            )}
          </>
        ) : (
          <form
            className='user'
            onSubmit={(event) => {
              event.preventDefault();
              sendSetupPhoneCode();
            }}
          >
            {setupPhoneError && (
              <p className='text-center' style={{color: '#FF0004'}}>
                {setupPhoneError}
              </p>
            )}
            <div className='form-group'>
              <input
                type='tel'
                name='setupPhone'
                id='setupPhone'
                className='form-control form-control-user'
                placeholder={tr('Mobile number, e.g. +1 555 123 4567', 'Celular, p. ej. +1 555 123 4567')}
                autoComplete='tel'
                value={setupPhone}
                onChange={(event) => {
                  setSetupPhone(event.target.value);
                }}
                autoFocus
                required
              />
            </div>
            <p className='text-gray-700' style={{fontSize: '13px'}}>
              {tr('We will text a code to this number.', 'Enviaremos un código a este número.')}
            </p>
            <input
              type='submit'
              className='btn btn-primary btn-user btn-block'
              value={tr('Send code', 'Enviar código')}
              disabled={!setupPhone.trim()}
            />
            <p className='text-gray-600 mt-3' style={{fontSize: '12px'}}>
              {tr(
                'By adding this number you agree to receive sign-in code texts. Message frequency varies. ' +
                  'Message and data rates may apply. Reply STOP to any text to opt out, HELP for help.',
                'Al agregar este número acepta recibir textos con códigos de acceso. La frecuencia de mensajes ' +
                  'varía. Pueden aplicarse tarifas de mensajes y datos. Responda STOP a cualquier texto para ' +
                  'cancelar, HELP para ayuda.'
              )}{' '}
              <a href='https://intelliculture.com/terms-full/' target='_blank' rel='noopener noreferrer'>
                {tr('Terms', 'Términos')}
              </a>
              {' · '}
              <a href='https://intelliculture.com/privacy-policy/' target='_blank' rel='noopener noreferrer'>
                {tr('Privacy', 'Privacidad')}
              </a>
            </p>
            {emailFactor && (
              <button
                type='button'
                className='btn btn-link btn-sm btn-block'
                onClick={() => {
                  startCodeStep(codeStep.factors, true);
                }}
              >
                {tr(
                  `Use email instead (${emailFactor.maskedDestination})`,
                  `Usar el correo (${emailFactor.maskedDestination})`
                )}
              </button>
            )}
          </form>
        )}
        <hr />
        <div className='text-center'>
          <button type='button' className='btn border bg-white small' onClick={leaveCodeStep}>
            {tr('Back', 'Volver')}
          </button>
        </div>
        {languageSwitch}
      </CoverPageCard>
    );
  }

  if (status == 'enterCode' || status == 'verifyingCode') {
    return (
      <CoverPageCard>
        <div className='text-center'>
          <h1 className='h4 text-gray-900 mb-2'>{tr('Enter your sign-in code', 'Ingrese su código de acceso')}</h1>
          {codeStep.enrollment && (
            <p className='text-gray-700' style={{fontSize: '14px'}}>
              {tr(
                'Your organization requires multi-factor authentication. Confirming this code also sets it up for ' +
                  'your account.',
                'Su organización exige la autenticación multifactor. Al confirmar este código también queda ' +
                  'configurada en su cuenta.'
              )}
            </p>
          )}
          {codeStep.maskedDestination && (
            <p className='text-gray-700' style={{fontSize: '14px'}}>
              {tr(
                `We sent a 6-digit code to ${codeStep.maskedDestination}`,
                `Enviamos un código de 6 dígitos a ${codeStep.maskedDestination}`
              )}
            </p>
          )}
        </div>
        <hr />
        {codeMessage.text && (
          <p className='text-center' style={{color: codeMessage.isError ? '#FF0004' : '#2E7D32'}}>
            {codeMessage.text}
          </p>
        )}
        <form className='user' onSubmit={handleCodeSubmit}>
          <div className='form-group'>
            <input
              type='text'
              name='signInCode'
              id='signInCode'
              className='form-control form-control-user text-center'
              placeholder='123456'
              inputMode='numeric'
              autoComplete='one-time-code'
              maxLength={6}
              value={codeInput}
              onChange={(event) => {
                setCodeInput(event.target.value.replace(/\D/g, '').slice(0, 6));
              }}
              style={{letterSpacing: '6px', fontSize: '20px'}}
              autoFocus
              required
            />
          </div>
          <input
            type='submit'
            className='btn btn-primary btn-user btn-block'
            value={tr('Verify', 'Verificar')}
            disabled={status == 'verifyingCode' || codeInput.length !== 6}
          />
        </form>
        <div className='text-center mt-3'>
          <button
            type='button'
            className='btn border bg-white small'
            disabled={codeStep.sending || resendWaitSec > 0}
            onClick={() => {
              if (codeStep.phoneNumber) {
                return sendSignInCode('text', codeStep.phoneNumber);
              }
              return sendSignInCode(
                codeStep.factors.find((factor) => {
                  return factor.maskedDestination === codeStep.maskedDestination;
                })?.factorType
              );
            }}
          >
            {tr('Send a new code', 'Enviar otro código')}
            {resendWaitSec > 0 ? ` (${resendWaitSec}s)` : ''}
          </button>
        </div>
        {codeStep.phoneNumber && codeStep.factors.length > 0 && (
          <div className='text-center mt-2' style={{fontSize: '14px'}}>
            <button
              type='button'
              className='btn btn-link btn-sm'
              disabled={codeStep.sending || resendWaitSec > 0}
              onClick={() => {
                startCodeStep(codeStep.factors, true);
              }}
            >
              {tr('Email me instead', 'Enviar por correo')} ({codeStep.factors[0].maskedDestination})
            </button>
          </div>
        )}
        {codeStep.factors.length > 1 && (
          <div className='text-center mt-2' style={{fontSize: '14px'}}>
            {codeStep.factors.map((factor) => {
              return (
                <button
                  key={factor.factorType}
                  type='button'
                  className='btn btn-link btn-sm'
                  disabled={
                    codeStep.sending || resendWaitSec > 0 || factor.maskedDestination === codeStep.maskedDestination
                  }
                  onClick={() => {
                    return sendSignInCode(factor.factorType);
                  }}
                >
                  {factor.factorType === 'text'
                    ? tr('Text me instead', 'Enviar por texto')
                    : tr('Email me instead', 'Enviar por correo')}{' '}
                  ({factor.maskedDestination})
                </button>
              );
            })}
          </div>
        )}
        <hr />
        <div className='text-center'>
          <button type='button' className='btn border bg-white small' onClick={leaveCodeStep}>
            {tr('Back', 'Volver')}
          </button>
        </div>
        {languageSwitch}
      </CoverPageCard>
    );
  }

  if (status == 'selectCustomer') {
    return (
      <CoverPageCard>
        <div className='text-center'>
          <h1 className='h4 text-gray-900 mb-4'>{tr('Select Customer', 'Seleccione un cliente')}</h1>
          <p className='text-gray-700'>
            {tr('Your account has access to more than one customer.', 'Su cuenta tiene acceso a más de un cliente.')}
          </p>
        </div>
        <hr />
        {customerSelectError && (
          <p className='text-center' style={{color: '#FF0004'}}>
            {customerSelectError}
          </p>
        )}
        <SearchableSelectTable
          items={customerChoice.options.map((customerOption) => {
            return {
              key: customerOption.name,
              label: customerOption.organizationName,
              subLabel: customerOption.name,
            };
          })}
          selecting={customerSelecting}
          onSelect={customerSelectClick}
          searchPlaceholder='Search customers...'
          countNoun='customers'
        />
      </CoverPageCard>
    );
  }

  return (
    <CoverPageCard>
      <div className='text-center'>
        <h1 className='h4 text-gray-900 mb-4'>
          {tr('Welcome Back to IntelliCulture!', '¡Bienvenido de nuevo a IntelliCulture!')}
          <span>
            <DownStatusIcon login={true} small={true} />
          </span>
        </h1>
      </div>
      <hr />
      <form className='user' onSubmit={handleSubmit}>
        {status == 'failure' && errorType === 'username' && (
          <p className='text-center' style={{color: '#FF0004'}}>
            {tr('Username does not exist', 'Usuario no existe')}
          </p>
        )}
        {status == 'failure' && errorType === 'slowDown' && (
          <p className='text-center' style={{color: '#FF0004'}}>
            {tr(
              `Too many attempts. Please wait ${retryAfterSec} seconds and try again.`,
              `Demasiados intentos. Espere ${retryAfterSec} segundos e intente de nuevo.`
            )}
          </p>
        )}
        {status == 'failure' && errorType === 'lockedOut' && (
          <p className='text-center' style={{color: '#FF0004'}}>
            {tr(
              `${
                lockoutMfaRequired ? 'Your organization requires multi-factor authentication and too' : 'Too'
              } many wrong codes were entered. To sign in again, contact support@intelliculture.com or ${
                lockoutContacts.length > 0 ? lockoutContacts.join(', ') : 'your administrator'
              }.`,
              `${
                lockoutMfaRequired
                  ? 'Su organización exige la autenticación multifactor y se ingresaron demasiados'
                  : 'Se ingresaron demasiados'
              } códigos incorrectos. Para volver a iniciar sesión, contacte a support@intelliculture.com o a ${
                lockoutContacts.length > 0 ? lockoutContacts.join(', ') : 'su administrador'
              }.`
            )}
          </p>
        )}
        {status == 'failure' && errorType === 'codesRequiredNoEmail' && (
          <p className='text-center' style={{color: '#FF0004'}}>
            {tr(
              'Your organization requires multi-factor authentication, but your account has no email address. ' +
                'Ask your administrator to add one.',
              'Su organización exige la autenticación multifactor, pero su cuenta no tiene correo. ' +
                'Pida a su administrador que agregue uno.'
            )}
          </p>
        )}
        {status == 'failure' && errorType === 'signInExpired' && (
          <p className='text-center' style={{color: '#FF0004'}}>
            {tr(
              'Your sign-in expired. Please enter your password again.',
              'Su acceso venció. Ingrese su contraseña de nuevo.'
            )}
          </p>
        )}
        {status == 'failure' && errorType === 'credentials' && (
          <p className='text-center' style={{color: '#FF0004'}}>
            {tr('Wrong username or password', 'Usuario o contraseña incorrecta')}
          </p>
        )}
        {status == 'failure' && errorType === 'account' && (
          <p className='text-center' style={{color: '#FF0004'}}>
            {tr(
              'Account unavailable. Contact your administrator.',
              'Cuenta no disponible. Contacte a su administrador.'
            )}
          </p>
        )}
        {status == 'failure' && errorType === 'passkey_not_found' && (
          <p className='text-center' style={{color: '#FF0004'}}>
            {tr(
              'Passkey not recognized. Try password login or register a new passkey.',
              'Passkey no reconocido. Ingrese con su contraseña o registre un passkey nuevo.'
            )}
          </p>
        )}
        {status == 'failure' && (errorType === 'missing_challenge' || errorType === 'options_failed') && (
          <p className='text-center' style={{color: '#FF0004'}}>
            {tr('Passkey sign-in expired. Please try again.', 'El acceso con passkey expiró. Intente de nuevo.')}
          </p>
        )}
        {status == 'failure' &&
          (errorType === 'verification_failed' || errorType === 'missing_credential' || errorType === 'passkey') && (
            <p className='text-center' style={{color: '#FF0004'}}>
              {tr(
                'Passkey sign-in failed. Try password login.',
                'Falló el acceso con passkey. Ingrese con su contraseña.'
              )}
            </p>
          )}
        {status == 'failure' && !errorType && (
          <p className='text-center' style={{color: '#FF0004'}}>
            {tr('Wrong username or password', 'Usuario o contraseña incorrecta')}
          </p>
        )}
        <div className='form-group'>
          <input
            type='text'
            name='username'
            id='loginUsername'
            className='form-control form-control-user'
            placeholder={tr('Enter Username...', 'Ingrese su usuario...')}
            value={inputs.username}
            onChange={handleChange}
            required
          />
        </div>
        <div className='form-group' style={{position: 'relative'}}>
          <input
            type={showPassword ? 'text' : 'password'}
            name='password'
            id='loginPassword'
            className='form-control form-control-user'
            placeholder={tr('Password', 'Contraseña')}
            value={inputs.password}
            onChange={handleChange}
            required
          />
        </div>
        <div className='form-group' style={{marginTop: '-10px', marginBottom: '10px'}}>
          <input
            type='checkbox'
            id='showPasswordCheckbox'
            checked={showPassword}
            onChange={() => {
              return setShowPassword((prev) => {
                return !prev;
              });
            }}
            style={{marginRight: '6px'}}
          />
          <label htmlFor='showPasswordCheckbox' style={{fontSize: '15px', userSelect: 'none'}}>
            {tr('Show Password', 'Mostrar contraseña')}
          </label>
        </div>
        <input
          type='submit'
          className='btn btn-primary btn-user btn-block'
          value={tr('Sign In', 'Ingresar')}
          disabled={status == 'submitting'}
        />
      </form>
      {passkeySupported && (
        <>
          <div className='text-center my-2' style={{fontSize: '13px', color: '#666'}}>
            {tr('or', 'o')}
          </div>
          <button
            type='button'
            className='btn btn-outline-primary btn-user btn-block'
            disabled={status == 'submitting'}
            onClick={handlePasskeyLogin}
          >
            {tr('Sign in with Passkey', 'Ingresar con passkey')}
          </button>
        </>
      )}
      <hr />
      <div className='text-center'>
        <button
          type='button'
          className='btn border bg-white small'
          onClick={() => {
            return navigate('/forgot_password');
          }}
        >
          {tr('Forgot Password?', '¿Olvidó su contraseña?')}
        </button>
      </div>
      {languageSwitch}
    </CoverPageCard>
  );
}

export {Login};
